import React, { useEffect, useState, useCallback } from "react"
import {
  Box,
  Flex,
  HStack,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
} from "@chakra-ui/react"
import { CalendarIcon, ChevronDownIcon, RepeatIcon, CheckIcon } from "@chakra-ui/icons"
import {
  listChiMisReports,
  getChiMisReport,
} from "../../Http/Index"

interface CompanyBreakdown {
  companyId: string
  companyName: string
  gmcUserCount: number
}

interface ChiMisReportData {
  reportMonth: string
  monthLabel: string
  reportYear: number
  status: string
  totalGmcUsers: number
  companyBreakdown: CompanyBreakdown[]
  generatedAt: string | null
  errorMessage: string | null
}

interface Snapshot {
  reportMonth: string
  monthLabel: string
  status: string
  totalGmcUsers: number
}

export const ChiMisReport: React.FC = () => {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [selectedMonth, setSelectedMonth] = useState("")
  const [reportData, setReportData] = useState<ChiMisReportData | null>(null)
  const [isLoadingList, setIsLoadingList] = useState(false)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [error, setError] = useState("")

  const fetchSnapshots = useCallback(async () => {
    setIsLoadingList(true)
    setError("")
    try {
      const res = await listChiMisReports(1, 48)
      const data = res.data?.data || []
      setSnapshots(data)
      if (data.length > 0 && !selectedMonth) {
        setSelectedMonth(data[0].reportMonth)
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch report list")
    } finally {
      setIsLoadingList(false)
    }
  }, [selectedMonth])

  useEffect(() => {
    fetchSnapshots()
  }, [fetchSnapshots])

  const fetchReport = useCallback(async (month: string) => {
    if (!month) return
    setIsLoadingReport(true)
    setError("")
    try {
      const res = await getChiMisReport(month)
      setReportData(res.data?.data || null)
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch report")
      setReportData(null)
    } finally {
      setIsLoadingReport(false)
    }
  }, [])

  useEffect(() => {
    if (selectedMonth) {
      fetchReport(selectedMonth)
    } else {
      setReportData(null)
    }
  }, [selectedMonth, fetchReport])

  const activeSnapshot = snapshots.find((s) => s.reportMonth === selectedMonth)

  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        CHI MIS Report — GMC User Count (Billing)
      </Text>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Modern Controls Card */}
      <Box
        bg="white"
        p={3}
        mb={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="xs"
      >
        <Flex gap={3} align="center" wrap="wrap">
          <HStack spacing={2}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wide">
              Period:
            </Text>
            <Menu matchWidth placement="bottom-start">
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                bg="white"
                borderColor="gray.300"
                _hover={{ bg: "gray.50", borderColor: "gray.400" }}
                _active={{ bg: "gray.100" }}
                leftIcon={<CalendarIcon color="blue.500" />}
                rightIcon={<ChevronDownIcon color="gray.500" />}
                minW="220px"
                textAlign="left"
                fontWeight="normal"
              >
                <Flex justify="space-between" align="center" w="full" mr={1}>
                  <Text fontWeight="medium" color={selectedMonth ? "gray.800" : "gray.400"} fontSize="xs">
                    {activeSnapshot?.monthLabel || "Select Month"}
                  </Text>
                  {activeSnapshot && (
                    <Badge colorScheme="blue" variant="subtle" fontSize="2xs" px={1.5} py={0.5} borderRadius="full">
                      {activeSnapshot.totalGmcUsers} users
                    </Badge>
                  )}
                </Flex>
              </MenuButton>
              <MenuList
                maxH="280px"
                overflowY="auto"
                boxShadow="lg"
                borderColor="gray.200"
                py={1}
                minW="240px"
                zIndex={10}
              >
                {snapshots.length === 0 ? (
                  <MenuItem isDisabled fontSize="xs">No snapshots available</MenuItem>
                ) : (
                  snapshots.map((s) => {
                    const isSelected = s.reportMonth === selectedMonth
                    return (
                      <MenuItem
                        key={s.reportMonth}
                        onClick={() => setSelectedMonth(s.reportMonth)}
                        bg={isSelected ? "blue.50" : "transparent"}
                        _hover={{ bg: isSelected ? "blue.100" : "gray.50" }}
                        py={2}
                        px={3}
                      >
                        <Flex justify="space-between" align="center" w="full">
                          <HStack spacing={2}>
                            {isSelected ? (
                              <CheckIcon color="blue.600" boxSize={2.5} />
                            ) : (
                              <Box w={2.5} />
                            )}
                            <Text fontSize="xs" fontWeight={isSelected ? "bold" : "medium"} color={isSelected ? "blue.700" : "gray.800"}>
                              {s.monthLabel}
                            </Text>
                          </HStack>
                          <Badge
                            colorScheme={isSelected ? "blue" : "gray"}
                            variant="subtle"
                            fontSize="2xs"
                            px={2}
                            py={0.5}
                            borderRadius="full"
                          >
                            {s.totalGmcUsers.toLocaleString()} users
                          </Badge>
                        </Flex>
                      </MenuItem>
                    )
                  })
                )}
              </MenuList>
            </Menu>
          </HStack>

          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            leftIcon={<RepeatIcon />}
            onClick={() => fetchSnapshots()}
            isLoading={isLoadingList}
            fontSize="xs"
          >
            Refresh
          </Button>
        </Flex>
      </Box>

      {/* Loading */}
      {isLoadingReport && (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      )}

      {/* No selection message */}
      {!isLoadingReport && !selectedMonth && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Select a month to view the CHI MIS report.
        </Alert>
      )}

      {/* Report Display */}
      {!isLoadingReport && reportData && (
        <Box>
          {/* Summary Stats */}
          <StatGroup mb={4} p={4} bg="gray.50" borderRadius="md" borderWidth={1}>
            <Stat>
              <StatLabel>Report Month</StatLabel>
              <StatNumber fontSize="lg">{reportData.monthLabel}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total GMC Users</StatLabel>
              <StatNumber fontSize="lg" color="blue.600">{reportData.totalGmcUsers.toLocaleString()}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Companies</StatLabel>
              <StatNumber fontSize="lg">{reportData.companyBreakdown.length}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Generated At</StatLabel>
              <StatNumber fontSize="sm">
                {reportData.generatedAt
                  ? new Date(reportData.generatedAt).toLocaleString()
                  : "N/A"}
              </StatNumber>
            </Stat>
          </StatGroup>

          {reportData.errorMessage && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {reportData.errorMessage}
            </Alert>
          )}

          {/* Company Breakdown Table */}
          {reportData.companyBreakdown.length > 0 && (
            <TableContainer borderWidth={1} borderRadius="md">
              <Table variant="simple" size="sm">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>#</Th>
                    <Th>Company Name</Th>
                    <Th isNumeric>GMC User Count</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {reportData.companyBreakdown.map((row, idx) => (
                    <Tr key={row.companyId}>
                      <Td>{idx + 1}</Td>
                      <Td>{row.companyName}</Td>
                      <Td isNumeric fontWeight="semibold">{row.gmcUserCount.toLocaleString()}</Td>
                    </Tr>
                  ))}
                  {/* Total Row */}
                  <Tr bg="blue.50" fontWeight="bold">
                    <Td></Td>
                    <Td>Total</Td>
                    <Td isNumeric>{reportData.totalGmcUsers.toLocaleString()}</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          )}

          {reportData.companyBreakdown.length === 0 && reportData.status === "COMPLETED" && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              No GMC users found for this month.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  )
}
